import AgentConversationsAPI from '../../api/agentConversations';
import { throwErrorMessage } from '../utils/api';

const PAGE_SIZE = 50;

const state = {
  messagesByConversationId: {},
  hasMoreByConversationId: {},
  uiFlags: {},
};

const getters = {
  getMessages: _state => conversationId =>
    _state.messagesByConversationId[conversationId] || [],
  getHasMore: _state => conversationId =>
    _state.hasMoreByConversationId[conversationId] !== false,
  getUIFlags: _state => conversationId =>
    _state.uiFlags[conversationId] || { fetching: false, sending: false },
};

const mutations = {
  SET_MESSAGES(_state, { conversationId, messages }) {
    _state.messagesByConversationId = {
      ..._state.messagesByConversationId,
      [conversationId]: messages,
    };
  },
  PREPEND_MESSAGES(_state, { conversationId, messages }) {
    const existing = _state.messagesByConversationId[conversationId] || [];
    _state.messagesByConversationId = {
      ..._state.messagesByConversationId,
      [conversationId]: [...messages, ...existing],
    };
  },
  APPEND_MESSAGE(_state, { conversationId, message }) {
    const existing = _state.messagesByConversationId[conversationId] || [];
    if (existing.find(m => m.id === message.id)) return;
    _state.messagesByConversationId = {
      ..._state.messagesByConversationId,
      [conversationId]: [...existing, message],
    };
  },
  MARK_CONVERSATION_READ(_state, conversationId) {
    const messages = _state.messagesByConversationId[conversationId];
    if (!messages) return;
    const now = Math.floor(Date.now() / 1000);
    _state.messagesByConversationId = {
      ..._state.messagesByConversationId,
      [conversationId]: messages.map(m =>
        m.read_at == null ? { ...m, read_at: now } : m
      ),
    };
  },
  SET_HAS_MORE(_state, { conversationId, hasMore }) {
    _state.hasMoreByConversationId = {
      ..._state.hasMoreByConversationId,
      [conversationId]: hasMore,
    };
  },
  SET_UI_FLAG(_state, { conversationId, flags }) {
    _state.uiFlags = {
      ..._state.uiFlags,
      [conversationId]: { ...(_state.uiFlags[conversationId] || {}), ...flags },
    };
  },
};

const actions = {
  fetchMessages: async ({ commit }, { conversationId, beforeId } = {}) => {
    commit('SET_UI_FLAG', { conversationId, flags: { fetching: true } });
    try {
      const { data } = await AgentConversationsAPI.getMessages(conversationId, {
        beforeId,
      });
      const messages = data.payload || [];
      if (beforeId) {
        commit('PREPEND_MESSAGES', { conversationId, messages });
        if (messages.length < PAGE_SIZE) {
          commit('SET_HAS_MORE', { conversationId, hasMore: false });
        }
      } else {
        commit('SET_MESSAGES', { conversationId, messages });
        commit('SET_HAS_MORE', {
          conversationId,
          hasMore: messages.length >= PAGE_SIZE,
        });
      }
      return messages;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { conversationId, flags: { fetching: false } });
    }
  },

  sendMessage: async (
    { commit },
    { conversationId, content, signedIds = [] }
  ) => {
    commit('SET_UI_FLAG', { conversationId, flags: { sending: true } });
    try {
      const { data } = await AgentConversationsAPI.sendMessage(conversationId, {
        content,
        signedIds,
      });
      commit('APPEND_MESSAGE', { conversationId, message: data });
      return data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { conversationId, flags: { sending: false } });
    }
  },

  receiveMessage: ({ commit, dispatch }, messageData) => {
    const conversationId = messageData.agent_conversation_id;
    commit('APPEND_MESSAGE', { conversationId, message: messageData });
    dispatch('agentConversations/receiveMessage', messageData, { root: true });
  },

  receiveReadReceipt: ({ commit }, { conversation_id: conversationId }) => {
    commit('MARK_CONVERSATION_READ', conversationId);
  },

  markRead: async ({ dispatch }, conversationId) => {
    try {
      await AgentConversationsAPI.markRead(conversationId);
      dispatch('agentConversations/clearUnread', conversationId, {
        root: true,
      });
    } catch (error) {
      throwErrorMessage(error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
